import {ViewportScroller} from '@angular/common';
import {HttpParams} from '@angular/common/http';
import {ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, ViewChildren} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {NbAccordionItemComponent} from '@nebular/theme';
import {
  BaseComponent,
  ConfirmDialogComponent,
  DamItem,
  FeatureTypes,
  isDateFeatureType as matchDateFeatureType,
  ProductTemplateFeatureValue,
  ProductWithBaseProduct,
  Template,
  TemplateWithProductValues,
} from 'compob2b-libs';
import {Observable, Subject} from 'rxjs';
import {distinctUntilChanged, filter, map, switchMap, takeUntil, tap} from 'rxjs/operators';
import {translations} from '../../../../../../environments/translations.ru';
import {DamItemTemplateFeaturesService, SummaryFeaturesInfo} from './dam-item-template-features.service';

@Component({
  selector: 'app-dam-item-template-features',
  templateUrl: './dam-item-template-features.component.html',
  styleUrls: ['./dam-item-template-features.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DamItemTemplateFeaturesComponent extends BaseComponent implements OnInit, OnDestroy {
  private loadTemplate = new Subject<void>();
  templates$: Observable<Template[]>;
  templateInput$ = new Subject<string>();
  loadingTemplates = false;
  isOpened = true;
  modelFeaturesChanges = false;
  selectedTemplate: Template;
  bindedTemplate: Template;
  modelFeatures: any = {};
  featureValues: ProductTemplateFeatureValue[];
  productFeatureStatus: SummaryFeaturesInfo;

  @ViewChildren('groupItem') groupAccordionItems: QueryList<NbAccordionItemComponent>;

  @Input() model: DamItem;
  @Input() isModal: boolean;
  @Input() createdDate: Date;

  @Output() closeModalEvent = new EventEmitter<void>();
  @Output() backEvent = new EventEmitter<void>();
  @Output() featureUnsavedChangesEvent = new EventEmitter<boolean>();
  @Output() saveEvent = new EventEmitter<boolean>();

  baseProductIdForm = new FormGroup({
    productId: new FormControl(null, [Validators.required, Validators.pattern('^\\d+$')])
  });

  constructor(protected readonly templateFeatureService: DamItemTemplateFeaturesService,
              private readonly scroller: ViewportScroller) {
    super();
  }

  ngOnInit(): void {
    this.selectedTemplate = this.bindedTemplate;
    this.loadTemplates();
    this.loading = true;

    this.loadTemplate
      .pipe(
        switchMap(_ => this.loadProductTemplate().pipe(takeUntil(this.unsubscribe)))
      )
      .subscribe(response => {
        this.loading = false;
        this.bindedTemplate = response ? response.template : null;
        const template = response ? response.template : null;
        this.featureValues = response && response.featureValues ? response.featureValues : [];
        this.selectedTemplate = template ? {id: template.id, header: template.header, enabled: template.enabled} : null;
        this.cdr.detectChanges();
      }, error => {
        this.handleError(error);
      });

    this.templateFeatureService.isFeaturesModelChanged()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(isChanged => this.featureUnsavedChangesEvent.emit(isChanged));

    this.templateFeatureService.getProductFeaturesStatus()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(item => this.productFeatureStatus = item);

    this.featureUnsavedChangesEvent
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(modelFeaturesChanges => this.modelFeaturesChanges = modelFeaturesChanges);

    this.loadTemplate.next();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.templateFeatureService.clearState();
  }

  private loadTemplates(): void {
    this.templates$ = this.templateInput$
      .pipe(
        distinctUntilChanged(),
        filter(term => term !== null),
        tap(_ => this.loadingTemplates = true),
        switchMap(term => this.searchTemplates(term).pipe(
          tap(_ => this.loadingTemplates = false),
        )),
      );
  }

  private loadProductTemplate(): Observable<TemplateWithProductValues> {
    return this.templateFeatureService.loadProductTemplate(this.model.id);
  }

  private bindTemplate(template: Template): void {
    this.templateFeatureService.bindTemplate(template.id, this.model.id)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(_ => {
        this.loadTemplate.next();
        this.saveEvent.emit(true);
      });
  }

  private replaceTemplate(template: Template): void {
    this.templateFeatureService.replaceTemplate(template.id, this.model.id)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(_ => {
        this.loadTemplate.next();
        this.saveEvent.emit(true);
      });
  }

  private searchTemplates(keyword: string): Observable<Template[]> {
    const params = new HttpParams().set('header', keyword);
    return this.compoApiService.getWithParams<Template[]>('/template/autocomplete/10', params)
      .pipe(map(response => response.data));
  }

  public saveFeatureModel(): void {
    if (!this.productFeatureStatus || this.productFeatureStatus.error) {
      return;
    }

    this.loading = true;
    this.templateFeatureService.saveProductTemplateFeatures(this.model.id)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((response: ProductTemplateFeatureValue[]) => {
        this.loading = false;
        this.successToast(translations.updated);
        this.reloadFeatureValues(response);
      }, error => {
        this.handleError(error);
      });
  }

  modalClose(): void {
    this.closeModalEvent.emit();
  }

  back(): void {
    this.backEvent.emit();
  }

  public toggleOpened() {

    this.isOpened = !this.isOpened;
  }

  changeSelectedTemplate(template: Template): void {
    if (this.bindedTemplate) {
      if (this.bindedTemplate.id === template.id) {
        return;
      }

      this.dialogService.open(ConfirmDialogComponent, {
        context: {
          description: translations.confirmRelpaceProductTemplate
        },
      }).onClose.pipe(
        takeUntil(this.unsubscribe)
      ).subscribe(result => {
        if (result) {
          this.selectedTemplate = template;
          this.templateFeatureService.clearState();
          this.replaceTemplate(template);
        } else {
          this.selectedTemplate = this.bindedTemplate;
        }
      });
    } else {
      this.bindTemplate(template);
    }
  }

  compareTemplates(a: Template, b: Template): boolean {
    return a.id === b.id;
  }

  scrollToGroup(groupIndex: number) {
    this.groupAccordionItems.get(groupIndex).open();
    this.scroller.scrollToAnchor('groupItem_' + groupIndex.toString());
  }

  isGroupFilled(groupID: number): boolean {
    return this.productFeatureStatus
      && groupID in this.productFeatureStatus.groups
      && this.productFeatureStatus.groups[groupID].empty === 0;
  }

  isGroupNotFilled(groupID: number): boolean {
    return this.productFeatureStatus
      && groupID in this.productFeatureStatus.groups
      && this.productFeatureStatus.groups[groupID].empty > 0;
  }

  emptyFeaturesInGroup(groupID: number): number {
    return this.productFeatureStatus
      && groupID in this.productFeatureStatus.groups
      && this.productFeatureStatus.groups[groupID].empty;
  }

  allFeaturesInGroup(groupID: number): number {
    return this.productFeatureStatus
      && groupID in this.productFeatureStatus.groups
      && this.productFeatureStatus.groups[groupID].all;
  }

  isSelectableFeatureType(featureTypeID: number): boolean {
    return [FeatureTypes.SELECT, FeatureTypes.CHECKBOX, FeatureTypes.COLOR].includes(featureTypeID);
  }

  isRangeFeatureType(featureTypeID: number): boolean {
    return featureTypeID === FeatureTypes.RANGE;
  }

  isBooleanFeatureType(featureTypeID: number): boolean {
    return featureTypeID === FeatureTypes.BOOLEAN;
  }

  isIconFeatureType(featureTypeID: number): boolean {
    return featureTypeID === FeatureTypes.ICON;
  }

  isIntervalFeatureType(featureTypeID: number): boolean {
    return featureTypeID === FeatureTypes.INTERVAL;
  }

  isStringFeatureType(featureTypeID: number): boolean {
    return featureTypeID === FeatureTypes.STRING;
  }

  isHtmlFeatureType(featureTypeID: number): boolean {
    return featureTypeID === FeatureTypes.HTML;
  }

  isSizeFeatureType(featureTypeID: number): boolean {
    return featureTypeID === FeatureTypes.SIZE;
  }

  isLinkFeatureType(featureTypeID: number): boolean {
    return featureTypeID === FeatureTypes.LINK;
  }

  isSmartlistFeatureType(featureTypeID: number): boolean {
    return featureTypeID === FeatureTypes.SMARTLIST;
  }

  isDateFeatureType(featureTypeID: number, featureTypeCode?: string): boolean {
    return matchDateFeatureType(featureTypeID, featureTypeCode);
  }

  isUnhandledFeatureType(featureTypeID: number, featureTypeCode?: string): boolean {
    return ![
      FeatureTypes.SELECT,
      FeatureTypes.CHECKBOX,
      FeatureTypes.COLOR,
      FeatureTypes.RANGE,
      FeatureTypes.BOOLEAN,
      FeatureTypes.ICON,
      FeatureTypes.INTERVAL,
      FeatureTypes.STRING,
      FeatureTypes.HTML,
      FeatureTypes.SIZE,
      FeatureTypes.LINK,
      FeatureTypes.SMARTLIST,
      FeatureTypes.DELIMITER,
    ].includes(featureTypeID) && !matchDateFeatureType(featureTypeID, featureTypeCode);
  }

  copyFeatureValues(): void {
    if (this.baseProductIdForm.invalid) {
      return;
    }

    this.loading = true;

    const entity: ProductWithBaseProduct = {
      productId: this.model.id,
      baseProductId: parseInt(this.baseProductIdForm.get('productId').value, 10),
    };

    this.compoApiService.post<ProductWithBaseProduct, ProductTemplateFeatureValue[]>('/product/copy-feature-values', entity)
      .pipe(
        map(response => response.data),
        takeUntil(this.unsubscribe)
      )
      .subscribe(success => this.handleSuccess(success), error => this.handleError(error));
  }

  private handleSuccess(response: ProductTemplateFeatureValue[]): void {
    this.loading = false;
    this.baseProductIdForm.reset();
    this.successToast(translations.updated);
    this.reloadFeatureValues(response);
  }

  private reloadFeatureValues(values: ProductTemplateFeatureValue[]): void {
    this.featureValues = values ? values : [];
    this.templateFeatureService.reloadProductFeatureValues(this.bindedTemplate, this.featureValues);
    this.featureUnsavedChangesEvent.emit(false);
    this.saveEvent.emit(true);
    this.cdr.detectChanges();
  }
}
